import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import { FiX, FiChevronDown, FiCheck } from 'react-icons/fi';
import SkillBadge from './skillBadge';

// Helper to extract id and label from data
const getId = (item) => {
  if (!item) return '';
  return typeof item === 'object' && (item.id || item.value || item.key)
    ? item.id || item.value || item.key
    : item;
};
const getLabel = (item) => {
  if (!item) return '';
  return typeof item === 'string' ? item : item.label || item.name || item.value || '';
};

const valueToObject = (item) => {
  if (!item) return null;
  if (typeof item === 'object') {
    return {
      id: getId(item),
      label: getLabel(item),
      ...item,
    };
  }
  return {
    id: item,
    label: String(item),
  };
};

const MultiSelectCandidateDropdown = ({
  label,
  options = [],
  selected,
  onChange,
  name,
  isMultiSelect = true,
  isLoading = false,
  placeholder = '',
  disabled = false,
  className = '',
  closeOnSelect = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [showMoreDropdown, setShowMoreDropdown] = useState(false);
  const moreDropdownRef = useRef(null);
  const containerRef = useRef(null);

  // Always normalize selectedArray to array of { id, label, ... }
  let selectedArray = [];
  if (isMultiSelect) {
    if (Array.isArray(selected)) {
      selectedArray = selected.map(valueToObject).filter(Boolean);
    } else if (selected) {
      selectedArray = [valueToObject(selected)];
    } else {
      selectedArray = [];
    }
  } else {
    if (name == 'nationality' && disabled) {
      selectedArray = null;
    } else if (selected) {
      selectedArray = valueToObject(selected);
    } else {
      selectedArray = null;
    }
  }

  const isSelected = useCallback(
    (option) => {
      const optionId = getId(option);
      if (isMultiSelect) {
        return selectedArray.some((sel) => getId(sel) === optionId);
      }
      return selectedArray && getId(selectedArray) === optionId;
    },
    [selectedArray, isMultiSelect]
  );

  const handleClickOutside = useCallback((e) => {
    if (containerRef.current && !containerRef.current.contains(e.target)) {
      setIsOpen(false);
      setSearchTerm('');
    }
    if (
      moreDropdownRef.current &&
      !moreDropdownRef.current.contains(e.target)
    ) {
      setShowMoreDropdown(false);
    }
  }, []);

  useEffect(() => {
    if (isOpen || showMoreDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen, showMoreDropdown, handleClickOutside]);

  // ── For multi-select: show ALL options with tick marks on selected ones.
  // ── For single-select: filter out already selected + apply search filter.
  const filteredOptions = useMemo(() => {
    const term = searchTerm.toLowerCase().trim();

    if (isMultiSelect) {
      // Show all options, with selected ones marked (not hidden)
      if (!term) return options;
      return options.filter((opt) => {
        const label = getLabel(opt).toLowerCase();
        return label.startsWith(term) || label.includes(term);
      });
    } else {
      // Single-select: filter out already selected + apply search
      if (!term) return options;
      return options.filter((opt) => {
        const label = getLabel(opt).toLowerCase();
        const optionId = getId(opt);
        if (selectedArray && getId(selectedArray) === optionId) return false;
        return label.startsWith(term) || label.includes(term);
      });
    }
  }, [options, searchTerm, selectedArray, isMultiSelect]);

  const handleSelect = useCallback(
    (option) => {
      const obj = valueToObject(option);
      if (!obj) return;

      if (isMultiSelect) {
        const exists = selectedArray.some((sel) => getId(sel) === obj.id);
        let updated;
        if (exists) {
          // Deselect if already selected (toggle)
          updated = selectedArray.filter((sel) => getId(sel) !== obj.id);
        } else {
          updated = [...selectedArray, obj];
        }
        onChange({ target: { name, value: updated } });
        if (closeOnSelect) {
          setIsOpen(false);
          setSearchTerm('');
        }
      } else {
        const alreadySelected = selectedArray && getId(selectedArray) === obj.id;
        const updated = alreadySelected ? null : obj;
        onChange({ target: { name, value: updated } });
        setIsOpen(false);
        setSearchTerm('');
      }
      setSearchTerm('');
    },
    [onChange, name, selectedArray, isMultiSelect, closeOnSelect]
  );

  const handleRemove = useCallback(
    (item) => {
      const id = getId(item);
      if (!id) return;

      if (isMultiSelect) {
        const updated = selectedArray.filter((sel) => getId(sel) !== id);
        onChange({ target: { name, value: updated } });
      } else {
        onChange({ target: { name, value: null } });
      }
    },
    [onChange, name, selectedArray, isMultiSelect]
  );

  const renderChips = useCallback(() => {
    if (!isMultiSelect) {
      if (!selectedArray) return null;
      const label = getLabel(selectedArray);
      return label ? (
        <SkillBadge>
          {label}
          <button
            type="button"
            onClick={() => handleRemove(selectedArray)}
            className="ml-1 p-0 border-none bg-transparent cursor-pointer"
          >
            <FiX size={12} />
          </button>
        </SkillBadge>
      ) : null;
    }

    const maxVisible = 1;
    const displayValues = Array.isArray(selectedArray)
      ? selectedArray.filter(Boolean)
      : [];

    if (displayValues.length === 0) return null;

    const first = displayValues[0];
    const rest = displayValues.slice(maxVisible);

    return (
      <div className="flex items-center gap-2">
        <SkillBadge>
          {getLabel(first)}
          <button
            type="button"
            onClick={() => handleRemove(first)}
            className="ml-1 p-0 border-none bg-transparent cursor-pointer"
          >
            <FiX size={12} />
          </button>
        </SkillBadge>
        {rest.length > 0 && (
          <div className="relative" ref={moreDropdownRef}>
            <button
              type="button"
              className="text-xs px-2 py-1 rounded-full mph-primary mph-light flex items-center gap-1"
              onClick={() => setShowMoreDropdown((v) => !v)}
            >
              +{rest.length}
              <FiChevronDown size={14} />
            </button>
            {showMoreDropdown && (
              <div className="absolute left-0 mt-2 z-20 bg-white border border-gray-200 rounded shadow-lg min-w-[200px]">
                {rest.map((item, idx) => (
                  <div
                    key={getLabel(item) + '_dropdownitem_' + idx}
                    className="flex items-center justify-between mph-primary px-3 py-1 text-xs"
                    title={getLabel(item)}
                    style={{
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      // whiteSpace: "nowrap",
                      "font-size": "10px",
                      maxWidth: 200
                    }}
                  >
                    <span>{getLabel(item)}</span>
                    <button
                      type="button"
                      onClick={() => handleRemove(item)}
                      className="ml-1 p-0 border-none bg-transparent cursor-pointer"
                    >
                      <FiX size={12} />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    );
  }, [getLabel, handleRemove, selectedArray, isMultiSelect, showMoreDropdown]);

  return (
    <div className="relative w-full" ref={containerRef}>
      {label && <label className="text-xs font-semibold text-gray-700">{label}</label>}

      {!(name == 'dual_nationality' && disabled) && (
        <div className="flex flex-wrap gap-2 my-1">{renderChips()}</div>
      )}

      <div className="relative w-full md:w-3/4">
        <input
          type="text"
          placeholder={placeholder || (isMultiSelect ? 'Search and select to add...' : 'Search and select...')}
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          onFocus={() => setIsOpen(true)}
          disabled={isLoading || disabled}
          className={`w-full p-1 border border-gray-300 rounded text-xs focus:outline-none focus:ring-1 focus:ring-blue-500 ${className}`}
        />

        {isOpen && (
          <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded shadow-lg max-h-[10rem] overflow-y-auto" style={{ bottom: '100%' }}>
            {isLoading ? (
              <div className="p-2 text-xs text-gray-500">Loading...</div>
            ) : filteredOptions.length > 0 ? (
              filteredOptions.map((opt, idx) => {
                const optLabel = getLabel(opt);
                const selected = isSelected(opt);
                return (
                  <button
                    key={`${optLabel}_${getId(opt)}_${idx}`}
                    type="button"
                    onClick={() => handleSelect(opt)}
                    className={`w-full text-left px-3 py-2 text-xs flex items-center justify-between gap-2 transition-colors
                      ${selected
                        ? 'bg-blue-50 text-blue-700 font-semibold hover:bg-blue-100'
                        : 'hover:bg-gray-100 text-gray-700'
                      }`}
                  >
                    <span className="truncate">{optLabel}</span>
                    {selected && (
                      <FiCheck
                        size={13}
                        className="flex-shrink-0 text-blue-600"
                        aria-label="selected"
                      />
                    )}
                  </button>
                );
              })
            ) : (
              <div className="p-2 text-xs text-gray-500">No options found</div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default MultiSelectCandidateDropdown;